import { html } from 'htm/preact'
import { useState, useEffect } from 'preact/hooks'
import { SystemHealth } from './admin/SystemHealth.js'
import { SchedulerStatus } from './admin/SchedulerStatus.js'
import { SourcesMonitoring } from './admin/SourcesMonitoring.js'
import { PerformanceMetrics } from './admin/PerformanceMetrics.js'
import { api } from '../services/api.js'

export function AdminDashboard() {
  const [dashboardData, setDashboardData] = useState(null)
  const [healthData, setHealthData] = useState(null)
  const [performanceData, setPerformanceData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [refreshInterval, setRefreshInterval] = useState(30) // seconds
  const [autoRefresh, setAutoRefresh] = useState(true)

  useEffect(() => {
    loadAllData()
    
    if (autoRefresh) {
      const interval = setInterval(loadAllData, refreshInterval * 1000)
      return () => clearInterval(interval)
    }
  }, [autoRefresh, refreshInterval])

  const loadAllData = async () => {
    try {
      setError(null)
      console.log('Loading admin dashboard data...')
      console.log('Making API calls to:', {
        dashboard: '/admin/monitoring/dashboard',
        health: '/admin/monitoring/health', 
        performance: '/admin/monitoring/performance'
      })
      
      // Load all monitoring data in parallel
      const [dashboardResponse, healthResponse, performanceResponse] = await Promise.all([
        api.get('/admin/monitoring/dashboard'),
        api.get('/admin/monitoring/health'),
        api.get('/admin/monitoring/performance')
      ])
      
      console.log('Dashboard data loaded:', { dashboardResponse, healthResponse, performanceResponse })
      
      setDashboardData(dashboardResponse.data)
      setHealthData(healthResponse.data)
      setPerformanceData(performanceResponse.data)
      
    } catch (err) {
      console.error('Error loading dashboard data:', err)
      setError(`Failed to load dashboard data: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  const triggerScrapeAll = async () => {
    try {
      await api.post('/admin/scrape/trigger')
      // Refresh data after triggering scrape
      setTimeout(loadAllData, 2000)
    } catch (err) {
      console.error('Error triggering scrape:', err)
    }
  }

  const triggerSourceSync = async () => {
    try {
      await api.post('/admin/sources/sync')
      // Refresh data after sync
      setTimeout(loadAllData, 2000)
    } catch (err) {
      console.error('Error syncing sources:', err)
    }
  }

  if (loading && !dashboardData) {
    return html`
      <div class="flex justify-center items-center py-12">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        <span class="ml-3 text-gray-600 dark:text-gray-400">Loading dashboard...</span>
      </div>
    `
  }

  return html`
    <div class="space-y-6 p-6">
      <!-- Header -->
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
            Admin Dashboard
          </h1>
          <p class="text-gray-600 dark:text-gray-400">
            Monitor news aggregation system performance and health
          </p>
        </div>
        
        <div class="flex items-center gap-4">
          <!-- Auto-refresh controls -->
          <div class="flex items-center gap-2">
            <input
              type="checkbox"
              id="auto-refresh"
              checked=${autoRefresh}
              onChange=${(e) => setAutoRefresh(e.target.checked)}
              class="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500"
            />
            <label for="auto-refresh" class="text-sm text-gray-700 dark:text-gray-300">
              Auto-refresh every
            </label>
            <select
              value=${refreshInterval}
              onChange=${(e) => setRefreshInterval(parseInt(e.target.value))}
              class="text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700"
            >
              <option value="10">10s</option>
              <option value="30">30s</option>
              <option value="60">1m</option>
              <option value="300">5m</option>
            </select>
          </div>
          
          <!-- Action buttons -->
          <button
            onClick=${loadAllData}
            class="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
            </svg>
            Refresh
          </button>
          
          <button
            onClick=${triggerScrapeAll}
            class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
            </svg>
            Trigger Scrape
          </button>
          
          <button
            onClick=${triggerSourceSync}
            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
            </svg>
            Sync Sources
          </button>
        </div>
      </div>

      ${error && html`
        <div class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div class="flex">
            <svg class="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
            </svg>
            <div class="ml-3">
              <p class="text-sm text-red-600 dark:text-red-400">${error}</p>
            </div>
          </div>
        </div>
      `}

      <!-- System Health -->
      ${healthData && html`
        <${SystemHealth} data=${healthData} />
      `}

      <!-- Dashboard Grid -->
      <div class="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <!-- Scheduler Status -->
        ${dashboardData && html`
          <${SchedulerStatus} 
            schedulerData=${dashboardData.scheduler} 
            overallStats=${dashboardData.overall_stats}
          />
        `}
        
        <!-- Performance Metrics -->
        ${performanceData && html`
          <${PerformanceMetrics} data=${performanceData} />
        `}
      </div>

      <!-- Sources Monitoring -->
      ${dashboardData && html`
        <${SourcesMonitoring} 
          sources=${dashboardData.sources} 
          onRefresh=${loadAllData}
        />
      `}

      <!-- Last updated timestamp -->
      ${dashboardData && html`
        <div class="text-sm text-gray-500 dark:text-gray-400 text-center">
          Last updated: ${new Date(dashboardData.timestamp).toLocaleString()}
        </div>
      `}
    </div>
  `
}